import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ContactFormForm from "../../modules/ContactFormSection";
import { clearEmptyCampaignData } from "../../common/campaign-data-setter";
import type { Swiper as SwiperType } from "swiper";
import regulations from "./assets/Regulamin-promocji-Taniej-z-mBankiem-3-edycja.pdf";
import "./index.scss";

import logoMauto from "./assets/logo-mauto.svg";
import iconArrow from "./assets/ikona-strzalka.svg";
import heroAuto from "./assets/hero-auto.png";
import chooserAuto from "./assets/chooser-auto-n.png";
import iconCheckGreen from "./assets/ikona-check-green.png";
import iconCheckRed from "./assets/ikona-check-red.png";
import iconInfo from "./assets/ikona-info.png";
import benefitsCars from "./assets/auta.png";
import { Illustration, Stripe, Text, UnorderedList } from "@mbank-design/design-system/components";
import { CardZeroCostMass } from "@mbank-design/design-system/illustrations";

type BenefitCard = {
  modifier: string;
  title: string;
  items: string[];
  isClone?: boolean;
};

const BENEFIT_CARDS: BenefitCard[] = [
  {
    modifier: "1",
    title: "konto firmowe za\u00A00\u00A0zł",
    items: ["0\u00A0zł za\u00A0prowadzenie rachunku firmowego", "zarządzaj wygodnie finansami w\u00A0jednym miejscu"]
  },
  {
    modifier: "2",
    title: "oszczędność i\u00A0prostota dla\u00A0Twojej firmy",
    items: [
      "2000\u00A0zł korzyści",
      "finansowanie i\u00A0rachunek w\u00A0jednym banku – mniej formalności, więcej kontroli"
    ]
  },
  {
    modifier: "3",
    title: "szybka procedura",
    items: ["wszystko możesz załatwić zdalnie – wybierasz auto, podpisujesz umowę, korzystasz z\u00A0promocji"]
  },
  {
    modifier: "4",
    title: "wsparcie ekspertów",
    items: ["nasi doradcy są do\u00A0Twojej dyspozycji na\u00A0każdym etapie"]
  }
];

type SwiperBundle = typeof import("swiper").Swiper;

let swiperBundlePromise: Promise<SwiperBundle> | null = null;

const loadSwiperBundle = async (): Promise<SwiperBundle> => {
  if (!swiperBundlePromise) {
    swiperBundlePromise = Promise.all([import("swiper/bundle"), import("swiper/css/bundle")]).then(
      ([swiperModule]) => swiperModule.default
    );
  }

  return swiperBundlePromise;
};

const BtnArrow = () => (
  <img className="btn__arrow" src={iconArrow} alt="" aria-hidden="true" width={28} height={28} />
);

const ExpandableText = ({
  collapsedLabel = "rozwiń",
  expandedLabel = "zwiń",
  children
}: {
  collapsedLabel?: string;
  expandedLabel?: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);

  const toggle = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((value) => !value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      toggle(event);
    }
  };

  return (
    <>
      <span
        className="open-hidden-text"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        {open ? expandedLabel : collapsedLabel}
      </span>
      <span className={`hidden-text${open ? " is-open" : ""}`}>{children}</span>
    </>
  );
};

const BenefitCardSlide = ({ card, isMobile }: { card: BenefitCard; isMobile: boolean }) => (
  <li
    className={`benefit-card benefit-card--${card.modifier}${card.isClone ? " benefit-card--clone" : ""}${isMobile ? " swiper-slide" : ""
      }`}
    aria-hidden={card.isClone ? true : undefined}
  >
    <h3 className="benefit-card__title">{card.title}</h3>
    <ul className="benefit-card__list">
      {card.items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </li>
);

const TaniejZMb = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeBenefitDot, setActiveBenefitDot] = useState(0);
  const [isMobileBenefits, setIsMobileBenefits] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 991px)").matches : false
  );

  const headerRef = useRef<HTMLElement>(null);
  const benefitsSwiperRef = useRef<HTMLDivElement>(null);
  const swiperInstanceRef = useRef<SwiperType | null>(null);

  const closeMenu = useCallback(() => {
    setIsNavOpen(false);
  }, []);

  useEffect(() => {
    clearEmptyCampaignData();
  }, []);

  useEffect(() => {
    const onKeyUp = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    const onResize = () => {
      if (window.innerWidth >= 992) {
        closeMenu();
      }
    };

    document.addEventListener("keyup", onKeyUp);
    window.addEventListener("resize", onResize);

    return () => {
      document.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("resize", onResize);
    };
  }, [closeMenu]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991px)");
    const updateViewport = () => setIsMobileBenefits(mq.matches);

    updateViewport();
    mq.addEventListener("change", updateViewport);

    return () => mq.removeEventListener("change", updateViewport);
  }, []);

  useEffect(() => {
    if (!isMobileBenefits) {
      return undefined;
    }

    let mounted = true;
    let frameId = 0;

    const destroySwiper = () => {
      if (swiperInstanceRef.current) {
        swiperInstanceRef.current.destroy(true, true);
        swiperInstanceRef.current = null;
      }
    };

    const syncDots = (swiper: SwiperType) => {
      setActiveBenefitDot(swiper.realIndex % BENEFIT_CARDS.length);
    };

    const initSwiper = async () => {
      const benefitsEl = benefitsSwiperRef.current;

      if (!benefitsEl || !mounted || swiperInstanceRef.current) {
        return;
      }

      try {
        const Swiper = await loadSwiperBundle();

        if (!mounted || swiperInstanceRef.current || !benefitsSwiperRef.current) {
          return;
        }

        swiperInstanceRef.current = new Swiper(benefitsSwiperRef.current, {
          slidesPerView: 1.3,
          centeredSlides: true,
          spaceBetween: 16,
          loop: true,
          autoHeight: true,
          watchOverflow: true,
          breakpoints: {
            576: { slidesPerView: 1.55, spaceBetween: 20 },
            768: { slidesPerView: 1.85, spaceBetween: 24 }
          },
          on: {
            init: syncDots,
            slideChange: syncDots
          }
        });
      } catch (error) {
        console.error("Failed to initialize benefits Swiper", error);
      }
    };

    frameId = window.requestAnimationFrame(() => {
      initSwiper();
    });

    return () => {
      mounted = false;
      window.cancelAnimationFrame(frameId);
      destroySwiper();
    };
  }, [isMobileBenefits]);

  const toggleNav = () => {
    setIsNavOpen((value) => !value);
  };

  const goToBenefit = (index: number) => {
    swiperInstanceRef.current?.slideToLoop(index);
  };

  const benefitSlides = isMobileBenefits
    ? [...BENEFIT_CARDS, ...BENEFIT_CARDS.map((card) => ({ ...card, isClone: true }))]
    : BENEFIT_CARDS;

  return (
    <div className="page taniej-z-mb">
      <Helmet>
        <title>mAuto – sfinansuj samochód i zyskaj 2000 zł</title>
        <meta name="description" content="" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="mAuto – sfinansuj samochód i zyskaj 2000 zł" />
        <meta property="og:description" content="" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <header ref={headerRef} className={`site-header${isNavOpen ? " is-open" : ""}`} id="top">
        <div className="container site-header__inner">
          <Link className="site-header__logo" to="/" aria-label="mAuto – strona główna">
            <img src={logoMauto} alt="mAuto" width={196} height={64} />
          </Link>
          <button
            className="nav-toggle"
            type="button"
            aria-label={isNavOpen ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={isNavOpen}
            aria-controls="menu"
            onClick={toggleNav}
          >
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
          </button>
          <nav className="main-nav" id="menu" aria-label="Menu główne">
            <ul className="main-nav__list">
              <li>
                <a className="main-nav__link" href="#warunki" onClick={closeMenu}>
                  zasady promocji
                </a>
              </li>
              <li>
                <a className="main-nav__link" href="#dlaczego-warto" onClick={closeMenu}>
                  dlaczego warto?
                </a>
              </li>
              <li>
                <a className="main-nav__link" href="#jak-skorzystac" onClick={closeMenu}>
                  jak skorzystać z&nbsp;promocji?
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero" id="hero">
          <div className="container hero__inner">
            <div className="hero__intro">
              {/* <p className="hero__eyebrow">korzystny zwrot akcji z&nbsp;mBankiem</p> */}
              <h1 className="hero__title">
                sfinansuj samochód, spłacaj leasing z&nbsp;konta firmowego w&nbsp;mBanku
                <span className="hero__title-accent">i&nbsp;zyskaj 2000&nbsp;zł</span>
              </h1>
              {!isMobileBenefits ? (
                <p className="hero__lead">
                  Teraz z&nbsp;rachunkiem firmowym w&nbsp;mBanku <strong>zyskujesz jeszcze więcej!</strong>
                </p>) :
                (<>
                  <br/>
                  <br/>
                </>)
              }
            </div>
          </div>
        </section>

        <section className="terms-blue" id="warunki">
          <div className="container terms-blue__inner">
            <h2 className="terms-blue__title">
              wystarczy, że&nbsp;spełnisz
              <br />
              <span className="terms-blue__title-accent">2 proste warunki:</span>
            </h2>
            <ol className="terms-blue__steps">
              <li className="num-step">
                <div className="num-step__head">
                  <span className="num-step__num">1</span>
                  <span className="num-step__label">krok</span>
                </div>
                <p className="num-step__text">
                  spłacisz co&nbsp;najmniej <strong>dwie z&nbsp;trzech pierwszych rat leasingowych</strong> z&nbsp;rachunku
                  firmowego w&nbsp;mBanku
                </p>
              </li>
              <li className="num-step">
                <div className="num-step__head">
                  <span className="num-step__num">2</span>
                  <span className="num-step__label">krok</span>
                </div>
                <p className="num-step__text">
                  <strong>opłacaj pełne raty w&nbsp;terminie</strong>
                </p>
              </li>
            </ol>
            <a className="btn btn--accent terms-blue__cta" href="#formularz">
              zapytaj o&nbsp;ofertę
              <BtnArrow />
            </a>
          </div>
        </section>

        <section className="chooser" id="wybor-auta">
          <div className="container">
            <h2 className="section-title chooser__title">
              jeśli nie&nbsp;wybrałeś jeszcze auta,
              <br />
              możesz je&nbsp;<span className="chooser__title-accent">wybrać z&nbsp;naszej oferty mAuto</span>
            </h2>
            {!isMobileBenefits && (<p className="chooser__subtitle">
              Masz dwie możliwości. Wybierz opcję, która jest dla&nbsp;Ciebie najwygodniejsza
            </p>)}

            <div className="chooser__grid">
              <article className="choice-card choice-card--mauto">
                <div className="choice-card__body">
                  <div className="choice-card__text">
                    <h3 className="choice-card__title">
                      wybierz auto
                      <br />
                      na <img className="choice-card__logo" src={logoMauto} alt="mAuto" width={150} height={49} />
                    </h3>
                    <p className="choice-card__desc">
                      Znajdź samochód z&nbsp;naszej oferty i&nbsp;od&nbsp;razu złóż wniosek o&nbsp;leasing
                    </p>
                    <ul className="choice-card__list">
                      <li>
                        <img
                          className="choice-card__check"
                          src={iconCheckGreen}
                          alt=""
                          aria-hidden="true"
                          width={28}
                          height={28}
                        />
                        auta dostępne od&nbsp;ręki
                      </li>
                      <li>
                        <img
                          className="choice-card__check"
                          src={iconCheckGreen}
                          alt=""
                          aria-hidden="true"
                          width={28}
                          height={28}
                        />
                        finansowanie w&nbsp;jednym miejscu
                      </li>
                    </ul>
                  </div>
                  <figure className="choice-card__media" aria-hidden="true">
                    <img src={chooserAuto} alt="" width={739} height={548} />
                  </figure>
                </div>
                <Link className="btn btn--green choice-card__cta" to="/lista-pojazdow">
                  wybierz na&nbsp;mAuto <BtnArrow />
                </Link>
              </article>

              {!isMobileBenefits && (<>
              <div className="chooser__or" aria-hidden="true">
                <span>lub</span>
              </div>

              <article className="choice-card choice-card--own">
                <h3 className="choice-card__title">masz już wybrane auto?</h3>
                <p className="choice-card__desc">
                  Jeśli znalazłeś samochód u&nbsp;dealera lub na&nbsp;rynku, wypełnij formularz
                </p>
                <ul className="choice-card__list">
                  <li>
                    <img
                      className="choice-card__check"
                      src={iconCheckRed}
                      alt=""
                      aria-hidden="true"
                      width={28}
                      height={28}
                    />
                    nowe i&nbsp;używane pojazdy
                  </li>
                  <li>
                    <img
                      className="choice-card__check"
                      src={iconCheckRed}
                      alt=""
                      aria-hidden="true"
                      width={28}
                      height={28}
                    />
                    oferta przygotowana indywidualnie
                  </li>
                </ul>
                <a className="btn btn--accent choice-card__cta" href="#formularz">
                  wypełnij formularz <BtnArrow />
                </a>
              </article>
              </>
              )}
            </div>

            <p className="chooser__note">
              <img src={iconInfo} alt="" aria-hidden="true" width={24} height={24} />
              <div>
                Finansowanie tylko w&nbsp;PLN.
                <br />
                Promocja dotyczy leasingu pojazdów o&nbsp;wartości co&nbsp;najmniej 100&nbsp;000&nbsp;zł netto. Jeśli nie masz
                jeszcze rachunku firmowego w&nbsp;mBanku, możesz go założyć szybko i&nbsp;wygodnie przez Twojego opiekuna
                handlowego. Szczegóły promocji w&nbsp;
                <a href={regulations} target="_blank" rel="noopener noreferrer">
                  regulaminie
                </a>
                .
              </div>
            </p>
          </div>
        </section>

        <section className="lead" id="formularz">
          <div className="container lead__inner">
            <h2 className="lead__title">
              Jeżeli masz już wybrany samochód,
              <br />
              <strong>wypełnij formularz i&nbsp;otrzymaj propozycję finansowania</strong>
            </h2>
            <div className="lead__form">
              <ContactFormForm isDefaultContact />
            </div>
          </div>
        </section>

        <section className="benefits" id="dlaczego-warto">
          <div className="container">
            <h2 className="section-title benefits__title">dlaczego warto</h2>
            <div className="benefits__layout">
              <div className="benefits__visual" aria-hidden="true">
                <span className="benefits__ring" />
                <img className="benefits__cars" src={benefitsCars} alt="" />
              </div>
              <div
                className={`benefits__cards${isMobileBenefits ? " swiper" : ""}`}
                ref={isMobileBenefits ? benefitsSwiperRef : null}
              >
                <ul className={isMobileBenefits ? "swiper-wrapper" : "benefits__cards-list"}>
                  {benefitSlides.map((card, index) => (
                    <BenefitCardSlide
                      key={`${card.modifier}-${card.isClone ? "clone" : "main"}-${index}`}
                      card={card}
                      isMobile={isMobileBenefits}
                    />
                  ))}
                </ul>
              </div>
              {isMobileBenefits && (
                <div className="benefits__dots" role="tablist" aria-label="Wybierz korzyść">
                  {BENEFIT_CARDS.map((card, index) => (
                    <button
                      key={card.modifier}
                      className={`benefits__dot${activeBenefitDot === index ? " is-active" : ""}`}
                      type="button"
                      aria-label={`Korzyść ${index + 1}`}
                      onClick={() => goToBenefit(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="promo-steps" id="jak-skorzystac">
          <div className="container">
            <h2 className="section-title promo-steps__title">jak skorzystać z&nbsp;promocji?</h2>
            <ol className="promo-steps__list">
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">1</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  <strong>załóż rachunek firmowy</strong> przez Twojego doradcę leasingowego, jeśli nie&nbsp;jesteś
                  jeszcze klientem mBanku
                </p>
              </li>
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">2</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  <strong>wybierz auto i&nbsp;podpisz umowę leasingu lub&nbsp;najmu</strong> na&nbsp;wybrane auto albo
                  auto z&nbsp;oferty mAuto
                </p>
              </li>
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">3</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  <strong>spłać 2&nbsp;z&nbsp;3 pierwszych rat</strong> z&nbsp;konta firmowego w&nbsp;mBanku
                </p>
              </li>
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">4</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  w&nbsp;szóstym miesiącu Twojej umowy{" "}
                  <strong>dostaniesz przelew w&nbsp;wysokości 2000&nbsp;zł od&nbsp;mLeasing!</strong>
                </p>
              </li>
            </ol>
            <a className="btn btn--accent promo-steps__cta" href="#formularz">
              zapytaj o&nbsp;ofertę <BtnArrow />
            </a>
            <div className="promo-steps__footer">
              <p className="promo-steps__regulamin">
                <a href={regulations} target="_blank" rel="noopener noreferrer">
                  Regulamin
                </a>
              </p>
              <p className="promo-steps__legal">
                Informacja jest informacją handlową i&nbsp;nie&nbsp;stanowi oferty w&nbsp;rozumieniu przepisów Kodeksu
                cywilnego. Ma charakter jedynie poglądowy i&nbsp;pomocniczy w&nbsp;przygotowaniu właściwej umowy. Umowa
                może być zawarta nie&nbsp;wcześniej niż
                po&nbsp;podjęciu przez mLeasing sp. z&nbsp;o.o. pozytywnej decyzji o&nbsp;finansowaniu. mAuto jest
                serwisem internetowym należącym do&nbsp;mLeasing sp. z&nbsp;o.o., stroną umowy będzie mLeasing sp.
                z&nbsp;o.o.
              </p>
            </div>
          </div>
        </section>
        <section className="taniej-z-mb__pros">
                <Stripe width={isMobileBenefits ? 300 : 800} />
                <div className="taniej-z-mb__pros__container">
                    <div className="taniej-z-mb__pros__illustration">
                        <Illustration illustrationComponent={CardZeroCostMass} size={isMobileBenefits ? 354 : 420} />
                    </div>
                    <div className="taniej-z-mb__pros__content">
                        <Text as="h3" styledAs="h3" marginBottom={20}><span style={{ color: "#ff8600" }}>dlaczego warto założyć</span> konto firmowe w mBanku?</Text>
                        <UnorderedList spacing={12}>
                            <UnorderedList.Item>
                                <Text><span style={{ color: "#ae0000", fontWeight: "bold" }}> bezterminowo 0 zł</span> za <strong>prowadzenie konta</strong> firmowego</Text>
                            </UnorderedList.Item>
                            <UnorderedList.Item>
                                <Text><span style={{ color: "#ae0000", fontWeight: "bold" }}>bezterminowo 0 zł</span> za pakiet <strong>20 internetowych przelewów krajowych</strong> w PLN w każdym miesiącu</Text>
                            </UnorderedList.Item>
                            <UnorderedList.Item>
                                <Text><span style={{ color: "#ae0000", fontWeight: "bold" }}>bezterminowo 0 zł</span> za pakiet <strong>20 internetowych przelewów w EUR</strong> do krajów UE oraz Islandii, Norwegii</Text>
                            </UnorderedList.Item>
                            <UnorderedList.Item>
                                <Text><span style={{ color: "#ae0000", fontWeight: "bold" }}>0 zł</span>  za pierwszą kartę debetową, <strong>Mastercard Debit Business, Mastercard Business WOŚP lub Mastercard Business WOŚP Mobilna, Visa Business Debit</strong> wybraną w promocyjnym wniosku o konto</Text>
                            </UnorderedList.Item>
                            <UnorderedList.Item>
                                <Text><span style={{ color: "#ae0000", fontWeight: "bold" }}>0 zł</span> na 2 lata za <strong>mOrganizer Finansów</strong> w wersji Optymalnej</Text>
                            </UnorderedList.Item>
                            <UnorderedList.Item>
                                <Text><span style={{ color: "#ae0000", fontWeight: "bold" }}>0 zł</span> za otwarcie i prowadzenie konta walutowego dla firm (EUR lub USD) na 24 miesiące</Text>
                            </UnorderedList.Item>
                            <UnorderedList.Item>
                                <Text> profesjonalna obsługa księgowa za 1 zł miesięcznie przez 3 miesiące (a później 229 zł netto)</Text>
                            </UnorderedList.Item>
                        </UnorderedList>
                    </div>
                </div>
            </section>
      </main>

      
    </div>
  );
};

export default TaniejZMb;
